import {
  deserializeHtmlNode,
  getParentNode,
  isBlock,
  isInline,
  isType,
  liftNodes,
  TNodeEntry,
  // unwrapNodes,
} from '../plate/common';
import { createMyPluginFactory, isMyElement } from '../plateFunctions';
import {
  ELEMENT_LI,
  ELEMENT_LIC,
  ELEMENT_PARAGRAPH,
  ELEMENT_TD,
  ELEMENT_TH,
  MyDescendant,
  MyEditor,
  MyElement,
  MyParagraphElement,
} from '../plateTypes';

type Options = {
  /** What block types to apply this plugin to */
  keys?: string[];
};

const withCustomNormalizer = (editor: MyEditor) => {
  const { normalizeNode } = editor;
  editor.normalizeNode = ([node, path]: TNodeEntry) => {
    if (!isMyElement(node)) return normalizeNode([node, path]);

    if (
      node.type === ELEMENT_PARAGRAPH &&
      (node as MyParagraphElement).children.length > 0
    ) {
      // Check if it's a paragraph inside a table cell, if so bail.
      if (path.length > 1) {
        const p = getParentNode(editor, path);
        if (
          p &&
          isType(editor, p[0], [
            ELEMENT_TD,
            ELEMENT_TH,
            // ELEMENT_LI,
            // ELEMENT_LIC,
          ])
        ) {
          return normalizeNode([node, path]);
        }

        // This is causing a crash, remove if import works as expected.
        //     // Can't have a P in a P, so unwrap it.
        //     unwrapNodes(editor, {
        //       at: path,
        //     });
        //     return normalizeNode([node, path]);
        return;
      }
      // No blocks in P's so lift em up.
      normalizeNode([node, path]);
      liftNodes(editor, {
        at: path,
        match(n, p) {
          return isBlock(editor, n) && p.length > 1;
        },
      });

      return;
    }

    // Call other normalizers
    normalizeNode([node, path]);
  };

  return editor;
};

/** Converts children of these blocks to text nodes */
export const createInjectDeserializeInlineChildrenPlugin =
  createMyPluginFactory<Options>({
    key: 'createInjectDeserializeInlineChildrenPlugin',
    options: {
      // keys is optional so providing a default value, but do override.
      keys: ['p'],
    },
    withOverrides: withCustomNormalizer,

    then(editor, plugin) {
      // Recreating deserializeHtmlNodeChildren from plate
      const deserializeHtmlNodes = deserializeHtmlNode(editor);
      const deserialize = {
        deserializeHtml: {
          getNode(element: HTMLElement, node: MyElement) {
            const children = Array.from(element.childNodes)
              .flatMap(deserializeHtmlNodes)
              .filter(Boolean)
              .flatMap((child, i) => {
                if (isInline(editor, child)) return child;
                if (typeof child === 'string') return { text: child };

                // UL/OL's can be nested inside LI/LIC's
                if (
                  // @ts-expect-error should be able to access type.
                  (child?.type === 'ul' || child?.type === 'ol') &&
                  (node.type === ELEMENT_LI || node.type === ELEMENT_LIC)
                ) {
                  return child;
                }
                if (isBlock(editor, child)) {
                  return [
                    insertElementBreak({
                      editor,
                      node: child as MyElement,
                      index: i,
                    }),
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore for some reason GH CI is failing on this.
                    ...(child as MyElement).children,
                  ].filter(Boolean);
                }
                return child;
              });

            return {
              ...node,
              children,
            };
          },
        },
      };

      // Apply to all keys
      const pluginsByKey = (plugin.options?.keys || []).reduce(
        (acc, key) => ({
          ...acc,
          [key]: deserialize,
        }),
        {},
      );

      return {
        // Inject because we want the plugin's initial deserializeHtml to run first
        inject: {
          pluginsByKey,
        },
      };
    },
  });

/**
 * Inserts the correct newline spacing to match margin from things like P tags,
 * If no margin or newline is needed, returns undefined.
 */
function insertElementBreak({
  editor,
  node,
  index,
}: {
  editor: MyEditor;
  node: MyElement;
  index: number;
}): MyDescendant | undefined {
  // If first element, no margin above.
  if (index === 0) return undefined;

  if ('type' in node) {
    if (
      node.type === ELEMENT_PARAGRAPH &&
      (node as MyParagraphElement).children.length > 0
    ) {
      // Filter out empty p's
      if (
        (node as MyParagraphElement).children.length === 1 &&
        (node as MyParagraphElement).children[0].text === ''
      )
        return undefined;
      return { text: '\n\n' };
    }

    // If inline, no newline.
    if (isInline(editor, node)) return undefined;

    // Most elements only start a newline, instead of having a gap above.
    return { text: '\n' };
  }

  return undefined;
}
